import { createContext, useMemo, useState } from "react";

import { IGroup } from "pages/[tenant]/g/@types";
import {
  IKeysToSearch,
  IKeysToSearchAuthor,
} from "pages/[tenant]/search/@types";
import { removeAccents } from "pages/[tenant]/search/utils/searchUtils";

import {
  IAuthor,
  ILesson,
  INewGlobalSearchContext,
  INewGlobalSearchContextProviderProps,
  ITag,
} from "shared/providers/context/types/NewGlobalSearch";
import { removeDuplicatedItems } from "shared/utils/removeDuplicatedItems";

export const NewGlobalSearch = createContext<INewGlobalSearchContext>(
  {} as INewGlobalSearchContext,
);

const keysToUseToSearchTerms: IKeysToSearch[] = ["title", "subtitle"];
const keysToUseToSearchAuthorTerms: IKeysToSearchAuthor[] = ["name", "email"];

export const NewGlobalSearchContextProvider = ({
  children,
}: INewGlobalSearchContextProviderProps) => {
  const [searchResult, setSearchResult] = useState(null);

  const [groups, setGroups] = useState<IGroup[]>([]);
  const [lessons, setLessons] = useState<ILesson[]>([]);

  const [itemsWasFounded, setItemsWasFounded] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const [authorsList, setAuthorsList] = useState([]);
  const [selectedAuthors, setSelectedAuthors] = useState([]);
  const [searchAuthorResult, setSearchAuthorResult] = useState<
    IAuthor[] | null
  >(null);
  const [authorWasFounded, setAuthorWasFounded] = useState(true);

  const [tagsList, setTagsList] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  const [isFiltering, setIsFiltering] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const listToIterate = useMemo(
    () => searchResult ?? groups,
    [searchResult, groups],
  );

  const authorToIterate = searchAuthorResult || authorsList;

  const concatGroupsLessons = useMemo(
    () => [...groups, ...lessons],
    [groups, lessons],
  );

  const searchInList = (term: string) => {
    setIsSearching(true);

    const termWithoutAccents = removeAccents(term).toLowerCase();

    const result = concatGroupsLessons.filter((item) => {
      const matchInKeys = keysToUseToSearchTerms.some((key) => {
        const itemValue = item[key];
        if (typeof itemValue !== "string") return false;

        const itemValueWithoutAccents = removeAccents(itemValue).toLowerCase();
        return itemValueWithoutAccents.includes(termWithoutAccents);
      });

      const matchInTags = item?.tags?.some((tag: ITag) => {
        const tagLabel = tag?.label || "";
        const tagLabelWithoutAccents = removeAccents(tagLabel).toLowerCase();
        return tagLabelWithoutAccents.includes(termWithoutAccents);
      });

      return matchInKeys || matchInTags;
    });

    setSearchResult(result?.length ? result : []);
    setItemsWasFounded(result?.length > 0);
  };

  const filterByAuthors = () => {
    const result = lessons.filter((item) =>
      item?.authors?.some((author: { id: number }) =>
        selectedAuthors?.some((selected) => selected.id === author.id),
      ),
    );

    const haveResult = !!result.length;
    if (haveResult) {
      setSearchResult(result);
      setItemsWasFounded(true);
    } else {
      setSearchResult(null);
      setItemsWasFounded(false);
    }
  };

  const filterByTags = () => {
    const result = concatGroupsLessons.filter((item) =>
      item?.tags?.some((tag: { id: number }) =>
        selectedTags?.some((selectedTag) => selectedTag.id === tag.id),
      ),
    );

    const haveResult = !!result.length;
    if (haveResult) {
      setSearchResult(result);
      setItemsWasFounded(true);
    } else {
      setSearchResult(null);
      setItemsWasFounded(false);
    }
  };

  const searchAuthors = (term: string) => {
    const result = authorsList.filter((item) =>
      keysToUseToSearchAuthorTerms.find((key) => {
        const itemNameWithoutAccents = removeAccents(
          item[key] || "",
        ).toLowerCase();
        const termWithoutAccents = removeAccents(term).toLowerCase();

        return itemNameWithoutAccents.includes(termWithoutAccents);
      }),
    );

    const haveAuthorsResult = !!result.length;
    if (haveAuthorsResult) {
      setSearchAuthorResult(result);
      setAuthorWasFounded(true);
    } else {
      setSearchAuthorResult(null);
      setAuthorWasFounded(false);
    }
  };

  const clearSearchResult = () => {
    setIsSearching(false);
    setItemsWasFounded(true);
    setSearchResult(null);
  };

  const clearSearchAuthor = () => {
    setAuthorWasFounded(true);
    setSearchAuthorResult(null);
  };

  const updateGroupsList = (newGroupsList: IGroup[]) => {
    if (groups?.length !== newGroupsList?.length) {
      setGroups((prev) =>
        [...prev, ...newGroupsList].reduce(removeDuplicatedItems, []),
      );
    } else {
      setGroups(newGroupsList);
    }
  };

  const updateLessonsList = (newLessonList) =>
    setLessons((prev) =>
      [...prev, ...newLessonList].reduce(removeDuplicatedItems, []),
    );

  const handleChangeModal = () => {
    setShowModal(!showModal);
  };


  return (
    <NewGlobalSearch.Provider
      value={{
        itemsWasFounded,
        authorToIterate,
        showModal,

        authorsList,
        setAuthorsList,
        selectedAuthors,
        setSelectedAuthors,
        authorWasFounded,
        searchAuthors,
        clearSearchAuthor,

        tagsList,
        setTagsList,
        selectedTags,
        setSelectedTags,

        isFiltering,
        setIsFiltering,

        isLoading,
        setIsLoading,

        isSearching,

        listToIterate,
        searchInList,
        filterByAuthors,
        filterByTags,

        clearSearchResult,
        updateGroupsList,
        updateLessonsList,
        handleChangeModal,
      }}
    >
      {children}
    </NewGlobalSearch.Provider>
  );
};
